import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { RSButton, RSFormLabel } from 'reactsymbols-kit';
import { Row, Col } from '../Grid';
import { useController, useMethodsForTransition, useInspectorChange } from '../../hooks';
import EditTransitionController from '../../merode/controllers/EditTransitionController';
import MethodPopover from '../MethodPopover';
import IconButton from '../IconButton';
import { Edit, CheckMark } from '../Icons';
import MethodSelectView from '../MethodSelect';
import Styles from '../../styles/colors';
import type { ITransition } from '../../stores/model/metamodel/transition';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { ITransitionInspector } from '../../stores/ui/transition_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type { IModel } from '../../stores/model';
import type Report from '../../merode/checks/Report';

// Wrap the ModelSelect component in observer
const MethodSelect = observer(MethodSelectView);

/**
 * Utility component for the buttons at the bottom of the transition inspector.
 *
 * Props:
 * - dirty: are there pending changes?
 * - onApply: callback to apply pending changes
 * - onDiscard: callback to discard pending changes
 * - onDelete: callback to delete the transition
 */
/** Props for the buttons at the bottom of an editable inspector. */
interface InspectorButtonsProps {
  /** Are there pending changes? */
  dirty: boolean;
  /** Callback to apply pending changes. */
  onApply: () => void;
  /** Callback to discard pending changes. */
  onDiscard: () => void;
  /** Callback to delete the element. */
  onDelete: () => void;
}

const TransitionInspectorButtons = ({ dirty, onApply, onDiscard, onDelete }: InspectorButtonsProps) => {
  if (dirty) {
    return (
      <div className="edit-transition p-[15px]">
        <Row gutter={5}>
          <Col md={6}>
            <RSButton
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              value="Discard"
              onClick={onDiscard}
            />
          </Col>
          <Col md={6}>
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              value="Apply changes"
              onClick={onApply}
            />
          </Col>
        </Row>
      </div>
    );
  } else {
    return (
      <div className="remove-transition p-[15px]">
        <RSButton
          type="button"
          background={Styles.redGradient}
          value="Remove transition"
          onClick={onDelete}
        />
      </div>
    );
  }
};

/**
 * Render a method in a transition
 *
 * Add a popover showing more information about the method.
 *
 * Props:
 * - method: The method
 */
const TransitionMethod = ({ method, dirty }: { method: IMethod; dirty?: boolean }) => {
  return (
    <MethodPopover preferPlace="left" method={method}>
      {/* `.methods.dirty div:not(.method-selector) .method` recoloured the
          read-only method list accent when dirty. The editor (.method-selector)
          and this read-only list never render together, so apply accent here. */}
      <div className={dirty ? 'method text-accent' : 'method'}>{method.asString()}</div>
    </MethodPopover>
  );
};

/**
 * Transition inspector.
 *
 * Includes an inline editor for the methods of the transition.
 *
 * Props:
 * - transition: the transition
 * - ui: The TransitionInspector ui store
 * - version: The ModelVersion (to pass to the controller)
 * - onReport: callback to call when a report is generated
 * - requestClose: callback to close the inspector
 */
interface TransitionInspectorProps {
  /** The transition to inspect. */
  transition: ITransition;
  /** The TransitionInspector ui store. */
  ui: ITransitionInspector;
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** Callback to close the inspector. */
  requestClose?: () => void;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
}

const TransitionInspector = ({ transition, ui, version, requestClose, onReport }: TransitionInspectorProps) => {
  // `useInspectorChange` indexes the change value by key on `transition`; the
  // edited key holds a `string[]`. Type the element param as that record surface
  // — same node at runtime, narrowing cast only.
  const [methods, onChangeMethods, methodsDirty] = useInspectorChange<string[]>(
    ui,
    transition as unknown as Record<string, string[]>,
    'metaMethodIds'
  );
  const [editingMethods, setEditingMethods] = useState(false);
  const controller = useController(
    () => new EditTransitionController(version, transition.fsm.objectType.id, transition.fsm.id)
  );

  // `version.content` is the controller-layer hand interface `IModel | null`;
  // the hook wants the MST `Instance` `IModel`. At runtime they are the same
  // loaded model node, and the version is always loaded (content non-null) here,
  // matching the original JS. Cast across the two `IModel` surfaces.
  const possibleMethods = useMethodsForTransition(
    version.content as unknown as IModel,
    transition.objectType.id,
    transition.sourceState.id,
    transition.targetState.id
  );

  // Apply the pending changes
  const onApply = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editTransition(transition.id, methods.slice());
    if (report.successful) {
      ui.discardChanges();
      setEditingMethods(false);
    }
    ui.setProjectBusy(false);
    if (onReport != null) {
      onReport(report);
    }
  };

  // Delete the transition
  const onDelete = async () => {
    ui.setProjectBusy(true);
    const report = await controller.deleteTransition(transition.id);
    if (requestClose != null) {
      requestClose();
    }
    ui.setProjectBusy(false);
    if (onReport != null) {
      onReport(report);
    }
  };

  const canEdit = transition.fsm.type !== 'DEFAULT';
  const methodsClassNames = ['methods'];
  if (methodsDirty) {
    methodsClassNames.push('dirty');
  }

  return (
    <div className="transition-inspector flex h-full flex-col">
      <div className="transition-content flex-1 overflow-auto px-[15px] py-5">
        <RSFormLabel>Object</RSFormLabel>
        <div>{transition.fsm.objectType.name}</div>

        <RSFormLabel>FSM</RSFormLabel>
        <div>{transition.fsm.name}</div>

        <RSFormLabel>Source state</RSFormLabel>
        <div>{transition.sourceState.name}</div>

        <RSFormLabel>Target state</RSFormLabel>
        <div>{transition.targetState.name}</div>

        <div className={methodsClassNames.join(' ')}>
          <div className="title-and-edit flex items-center">
            <div className="title inspector-subtitle flex-[1_1_auto]">Methods</div>
            {canEdit &&
              (editingMethods ? (
                <IconButton key="save" variant="inline" onClick={() => setEditingMethods(false)}>
                  <CheckMark />
                </IconButton>
              ) : (
                <IconButton key="edit" variant="inline" onClick={() => setEditingMethods(true)}>
                  <Edit />
                </IconButton>
              ))}
          </div>
          {!editingMethods ? (
            possibleMethods
              .filter((m) => methods.includes(m.id))
              .map((m) => <TransitionMethod key={m.id} method={m} dirty={methodsDirty} />)
          ) : (
            <MethodSelect methods={possibleMethods} selected={methods} onChange={onChangeMethods} />
          )}
        </div>
      </div>
      {canEdit && (
        <TransitionInspectorButtons
          dirty={ui.dirty}
          onApply={onApply}
          onDiscard={ui.discardChanges}
          onDelete={onDelete}
        />
      )}
    </div>
  );
};

// Wrap the view in observer
const ObservedTransitionInspector = observer(TransitionInspector);
export default ObservedTransitionInspector;
